import { useState } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { api, errMsg } from '../lib/api';
import { Input } from '../components/ui/input';
import { Button } from '../components/ui/button';
import { toast } from 'sonner';
import { PlugsConnected, Key, CloudArrowUp, Trash, Copy } from '@phosphor-icons/react';

const ST = (s) => s?.startsWith('live') ? 'bg-green-100 text-green-800' : s?.startsWith('configured') ? 'bg-amber-100 text-amber-800' : 'bg-slate-100 text-slate-500';
const copy = (t) => { navigator.clipboard.writeText(t); toast.success('Copied'); };

function PluginCard({ p, refetch }) {
  const [cfg, setCfg] = useState({ client_id: '', client_secret: '', refresh_token: '', org_id: '', dc: 'in', target_url: '' });
  const [open, setOpen] = useState(false);
  const [logs, setLogs] = useState(null);
  const [hook, setHook] = useState(null);
  const run = async (fn, okMsg) => { try { const r = await fn(); if (okMsg) toast.success(okMsg); refetch(); return r; } catch (e) { toast.error(errMsg(e)); } };
  const install = () => run(() => api.post('/integrations/install', { plugin: p.key }), `${p.name} installed`);
  const save = () => run(() => api.put(`/integrations/${p.key}/config`, Object.fromEntries(Object.entries(cfg).filter(([, v]) => v))), 'Configuration saved');
  const test = async () => { const r = await run(() => api.post(`/integrations/${p.key}/test`)); if (r) (r.data.ok ? toast.success : toast.error)(r.data.message); };
  const sync = async (res) => { const r = await run(() => api.post(`/integrations/${p.key}/sync`, { resource: res })); if (r) toast[r.data.status === 'error' ? 'error' : 'success'](`${r.data.status}: ${r.data.detail}`); };
  const loadLogs = async () => setLogs((await api.get(`/integrations/${p.key}/logs`)).data);
  const loadHook = async () => setHook((await api.get(`/integrations/${p.key}/webhook-info`)).data);
  const uninstall = () => { if (window.confirm(`Uninstall ${p.name}? Credentials will be deleted.`)) run(() => api.delete(`/integrations/${p.key}`), 'Uninstalled'); };
  const inst = p.installation;
  const isZoho = p.key.startsWith('zoho');
  return (
    <div className="bg-white border border-slate-200 rounded-xl p-5" data-testid={`plugin-card-${p.key}`}>
      <div className="flex items-start justify-between gap-3 flex-wrap">
        <div>
          <p className="font-display font-bold">{p.name} <span className="font-mono-label text-[10px] uppercase text-slate-400 ms-1">{p.category}</span></p>
          <p className="text-xs text-slate-500 mt-0.5">{p.syncs}</p>
          {inst && <span className={`inline-block mt-2 text-[11px] px-2 py-0.5 rounded ${ST(inst.status)}`} data-testid={`plugin-status-${p.key}`}>{inst.status}</span>}
        </div>
        {!p.installed
          ? <Button size="sm" data-testid={`plugin-install-${p.key}`} className="bg-slate-900 hover:bg-slate-800" onClick={install}>Install</Button>
          : <div className="flex gap-2">
              <Button size="sm" variant="outline" data-testid={`plugin-configure-${p.key}`} onClick={() => setOpen(!open)}>{open ? 'Close' : 'Configure'}</Button>
              <button data-testid={`plugin-uninstall-${p.key}`} onClick={uninstall} className="text-rose-500 hover:text-rose-700 px-1"><Trash size={16} /></button>
            </div>}
      </div>
      {p.installed && open && (
        <div className="mt-4 border-t border-slate-100 pt-4 space-y-3">
          {isZoho ? (
            <>
              <p className="text-xs text-slate-500">Create a <b>Self Client</b> at <a href="https://api-console.zoho.com" target="_blank" rel="noreferrer" className="underline">api-console.zoho.com</a>, then paste the credentials. Sync goes live the moment the connection test passes.</p>
              <div className="grid sm:grid-cols-2 gap-2">
                <Input data-testid={`cfg-client-id-${p.key}`} placeholder={`Client ID ${inst.config?.client_id ? `(saved: ${inst.config.client_id.slice(0, 12)}...)` : ''}`} value={cfg.client_id} onChange={(e) => setCfg({ ...cfg, client_id: e.target.value })} />
                <Input data-testid={`cfg-client-secret-${p.key}`} type="password" placeholder={inst.config?.has_secret ? 'Client Secret (saved - encrypted)' : 'Client Secret'} value={cfg.client_secret} onChange={(e) => setCfg({ ...cfg, client_secret: e.target.value })} />
                <Input data-testid={`cfg-refresh-token-${p.key}`} type="password" placeholder={inst.config?.has_refresh_token ? 'Refresh Token (saved - encrypted)' : 'Refresh Token'} value={cfg.refresh_token} onChange={(e) => setCfg({ ...cfg, refresh_token: e.target.value })} />
                <select data-testid={`cfg-dc-${p.key}`} value={cfg.dc} onChange={(e) => setCfg({ ...cfg, dc: e.target.value })} className="border border-slate-200 rounded-md px-2 py-2 text-sm bg-white">
                  {['in', 'com', 'eu', 'com.au', 'jp'].map((d) => <option key={d} value={d}>Zoho DC: .{d}</option>)}
                </select>
                {p.key === 'zoho_books' && <Input data-testid={`cfg-org-id-${p.key}`} placeholder={`Books Organization ID ${inst.config?.org_id ? `(saved: ${inst.config.org_id})` : ''}`} value={cfg.org_id} onChange={(e) => setCfg({ ...cfg, org_id: e.target.value })} />}
              </div>
            </>
          ) : (
            <Input data-testid={`cfg-target-url-${p.key}`} placeholder={inst.config?.target_url ? `Target URL (saved: ${inst.config.target_url})` : 'Target URL (https://...) - Zapier / Make / your endpoint'} value={cfg.target_url} onChange={(e) => setCfg({ ...cfg, target_url: e.target.value })} />
          )}
          <div className="flex flex-wrap gap-2">
            <Button size="sm" data-testid={`cfg-save-${p.key}`} className="bg-slate-900 hover:bg-slate-800" onClick={save}>Save config</Button>
            <Button size="sm" variant="outline" data-testid={`cfg-test-${p.key}`} onClick={test}>Test connection</Button>
            {p.resources.map((r) => <Button key={r} size="sm" data-testid={`cfg-sync-${p.key}-${r}`} className="bg-orange-600 hover:bg-orange-500" onClick={() => sync(r)}><CloudArrowUp size={14} className="me-1" />Sync {r}</Button>)}
            <Button size="sm" variant="outline" data-testid={`cfg-logs-${p.key}`} onClick={loadLogs}>Sync logs</Button>
            {isZoho && <Button size="sm" variant="outline" data-testid={`cfg-webhook-${p.key}`} onClick={loadHook}>Inbound webhook</Button>}
          </div>
          {inst.last_test && <p className={`text-xs ${inst.last_test.ok ? 'text-green-700' : 'text-amber-700'}`} data-testid={`last-test-${p.key}`}>Last test: {inst.last_test.message} ({inst.last_test.at?.slice(0, 16)})</p>}
          {hook && (
            <div className="bg-slate-50 rounded-md p-3 text-xs space-y-1" data-testid={`webhook-info-${p.key}`}>
              <p className="font-semibold">Point the vendor webhook here:</p>
              <p className="font-mono break-all">{hook.url} <button onClick={() => copy(hook.url)} className="text-orange-600"><Copy size={12} /></button></p>
              <p>Header <code>{hook.header}</code>: <span className="font-mono">{hook.secret}</span> <button onClick={() => copy(hook.secret)} className="text-orange-600"><Copy size={12} /></button></p>
            </div>
          )}
          {logs && (
            <div className="space-y-1" data-testid={`sync-logs-${p.key}`}>
              {logs.length === 0 && <p className="text-xs text-slate-400">No syncs yet.</p>}
              {logs.map((l) => (
                <p key={l.id} className="text-xs text-slate-600 border-b border-slate-50 py-1">
                  <span className={`px-1.5 rounded ${l.status === 'success' ? 'bg-green-100 text-green-800' : l.status === 'error' ? 'bg-rose-100 text-rose-700' : 'bg-slate-100'}`}>{l.status}</span> {l.at?.slice(0, 16)} · {l.resource} · {l.detail}
                </p>
              ))}
            </div>
          )}
        </div>
      )}
    </div>
  );
}

function ApiKeys() {
  const qc = useQueryClient();
  const [name, setName] = useState('');
  const [fresh, setFresh] = useState(null);
  const { data: keys } = useQuery({ queryKey: ['api-keys'], queryFn: () => api.get('/integrations/api-keys').then((r) => r.data) });
  const createMut = useMutation({
    mutationFn: () => api.post('/integrations/api-keys', { name }),
    onSuccess: (r) => { setFresh(r.data); setName(''); qc.invalidateQueries({ queryKey: ['api-keys'] }); },
    onError: (e) => toast.error(errMsg(e)),
  });
  const revokeMut = useMutation({
    mutationFn: (id) => api.delete(`/integrations/api-keys/${id}`),
    onSuccess: () => { toast.success('Key revoked'); qc.invalidateQueries({ queryKey: ['api-keys'] }); }, onError: (e) => toast.error(errMsg(e)),
  });
  return (
    <div className="space-y-4" data-testid="api-keys-tab">
      <div className="bg-white border border-slate-200 rounded-xl p-5">
        <p className="font-display font-bold mb-1">REST API keys</p>
        <p className="text-xs text-slate-500 mb-3">Read-only access to your tenant's data at <code>/api/public/v1/...</code> - students, invoices, staff. Full reference in Documentation - REST API.</p>
        <div className="flex gap-2">
          <Input data-testid="api-key-name-input" placeholder='Key name (e.g. "Zapier", "Data warehouse")' value={name} onChange={(e) => setName(e.target.value)} />
          <Button data-testid="api-key-create-btn" className="bg-slate-900 hover:bg-slate-800" disabled={!name.trim() || createMut.isPending} onClick={() => createMut.mutate()}>Create key</Button>
        </div>
        {fresh && (
          <div className="mt-3 bg-orange-50 border border-orange-200 rounded-md p-3 text-xs" data-testid="api-key-fresh">
            <p className="font-semibold text-orange-800">Copy this key now - it is shown once:</p>
            <p className="font-mono break-all mt-1">{fresh.key} <button onClick={() => copy(fresh.key)} className="text-orange-600"><Copy size={13} /></button></p>
          </div>
        )}
      </div>
      <div className="bg-white border border-slate-200 rounded-xl p-5" data-testid="api-keys-list">
        {(keys || []).length === 0 && <p className="text-sm text-slate-400">No API keys yet.</p>}
        {(keys || []).map((k) => (
          <div key={k.id} className="flex items-center justify-between border-b border-slate-50 py-2 text-sm">
            <div><b>{k.name}</b> <span className="font-mono text-xs text-slate-400 ms-2">{k.prefix}...</span>
              <p className="text-[11px] text-slate-400">created {k.created_at?.slice(0, 10)} · last used {k.last_used ? k.last_used.slice(0, 16) : 'never'}</p></div>
            <Button size="sm" variant="outline" data-testid={`api-key-revoke-${k.id}`} className="text-rose-600 border-rose-200" onClick={() => revokeMut.mutate(k.id)}>Revoke</Button>
          </div>
        ))}
      </div>
    </div>
  );
}

function ScimPanel() {
  const [tok, setTok] = useState(null);
  const { data: conns } = useQuery({ queryKey: ['iam-connectors'], queryFn: () => api.get('/iam/connectors').then((r) => r.data) });
  const issue = async () => { try { setTok((await api.post('/iam/connectors/scim/token')).data); } catch (e) { toast.error(errMsg(e)); } };
  const scim = (conns?.connectors || []).find((c) => c.type === 'scim');
  return (
    <div className="bg-white border border-slate-200 rounded-xl p-5 space-y-3" data-testid="scim-panel">
      <p className="font-display font-bold">SCIM 2.0 provisioning</p>
      <p className="text-xs text-slate-500">Auto-provision & deprovision users from Okta, Azure AD or Google Workspace. Endpoint: <code className="break-all">{scim?.endpoints?.[0]}</code></p>
      <p className="text-xs text-slate-500">Token status: {scim?.token_issued ? `issued ${scim.token_issued_at?.slice(0, 16)}` : 'not issued yet'}</p>
      <Button size="sm" data-testid="scim-issue-token" className="bg-slate-900 hover:bg-slate-800" onClick={issue}>{scim?.token_issued ? 'Rotate token' : 'Issue token'}</Button>
      {tok && <div className="bg-orange-50 border border-orange-200 rounded-md p-3 text-xs" data-testid="scim-token-fresh">
        <p className="font-semibold text-orange-800">Bearer token (shown once):</p>
        <p className="font-mono break-all mt-1">{tok.token} <button onClick={() => copy(tok.token)} className="text-orange-600"><Copy size={13} /></button></p>
      </div>}
    </div>
  );
}

function PaymentsPanel() {
  const qc = useQueryClient();
  const [f, setF] = useState({ key_id: '', key_secret: '', webhook_secret: '' });
  const { data: s } = useQuery({ queryKey: ['rzp-settings'], queryFn: () => api.get('/indiapay/settings').then((r) => r.data) });
  const save = async () => {
    try {
      await api.post('/indiapay/settings', Object.fromEntries(Object.entries(f).filter(([, v]) => v)));
      toast.success('Razorpay configured - guardians now see a UPI / Card button on invoices');
      setF({ key_id: '', key_secret: '', webhook_secret: '' });
      qc.invalidateQueries({ queryKey: ['rzp-settings'] });
    } catch (e) { toast.error(errMsg(e)); }
  };
  return (
    <div className="bg-white border border-slate-200 rounded-xl p-5 space-y-3" data-testid="payments-panel">
      <p className="font-display font-bold">Razorpay - UPI, cards & net-banking (India)</p>
      <p className="text-xs text-slate-500">Get keys from <a href="https://dashboard.razorpay.com" target="_blank" rel="noreferrer" className="underline">dashboard.razorpay.com</a> (Settings, API Keys). Test keys (rzp_test_...) work instantly. Payments auto-reconcile against invoices.</p>
      <p className="text-xs" data-testid="rzp-status">Status: {s?.configured ? <span className="text-green-700 font-semibold">configured ({s.key_id})</span> : <span className="text-slate-400">not configured - guardians see Stripe only</span>}</p>
      <div className="grid sm:grid-cols-3 gap-2">
        <Input data-testid="rzp-key-id" placeholder="Key ID (rzp_test_... / rzp_live_...)" value={f.key_id} onChange={(e) => setF({ ...f, key_id: e.target.value })} />
        <Input data-testid="rzp-key-secret" type="password" placeholder="Key Secret" value={f.key_id === '' && s?.configured ? f.key_secret : f.key_secret} onChange={(e) => setF({ ...f, key_secret: e.target.value })} />
        <Input data-testid="rzp-webhook-secret" type="password" placeholder="Webhook secret (optional)" value={f.webhook_secret} onChange={(e) => setF({ ...f, webhook_secret: e.target.value })} />
      </div>
      <Button data-testid="rzp-save" size="sm" className="bg-slate-900 hover:bg-slate-800" disabled={!f.key_id || !f.key_secret} onClick={save}>Save Razorpay keys</Button>
    </div>
  );
}

function SmsPanel() {
  const qc = useQueryClient();
  const [f, setF] = useState({ account_sid: '', auth_token: '', from_number: '', verify_sid: '' });
  const [testTo, setTestTo] = useState('');
  const { data: s } = useQuery({ queryKey: ['sms-settings'], queryFn: () => api.get('/sms/settings').then((r) => r.data) });
  const { data: log } = useQuery({ queryKey: ['sms-log'], queryFn: () => api.get('/sms/log').then((r) => r.data) });
  const save = async () => {
    try {
      await api.post('/sms/settings', Object.fromEntries(Object.entries(f).filter(([, v]) => v)));
      toast.success('Twilio configured');
      setF({ account_sid: '', auth_token: '', from_number: '', verify_sid: '' });
      qc.invalidateQueries({ queryKey: ['sms-settings'] });
    } catch (e) { toast.error(errMsg(e)); }
  };
  const test = async () => {
    try {
      const { data } = await api.post('/sms/test', { to: testTo });
      toast[data.status.startsWith('sent') ? 'success' : 'warning'](data.status);
      qc.invalidateQueries({ queryKey: ['sms-log'] });
    } catch (e) { toast.error(errMsg(e)); }
  };
  return (
    <div className="space-y-4" data-testid="sms-panel">
      <div className="bg-white border border-slate-200 rounded-xl p-5 space-y-3">
        <p className="font-display font-bold">Twilio - SMS & phone verification</p>
        <p className="text-xs text-slate-500">From <a href="https://console.twilio.com" target="_blank" rel="noreferrer" className="underline">console.twilio.com</a>: Account SID, Auth Token, a From number. Optionally a Verify Service SID for OTP. Until configured, SMS runs in stub mode (logged, not sent).</p>
        <p className="text-xs" data-testid="sms-status">Status: {s?.configured ? <span className="text-green-700 font-semibold">configured ({s.account_sid} · from {s.from_number || 'no number'})</span> : <span className="text-slate-400">not configured - stub mode</span>}</p>
        <div className="grid sm:grid-cols-2 gap-2">
          <Input data-testid="sms-sid" placeholder="Account SID (AC...)" value={f.account_sid} onChange={(e) => setF({ ...f, account_sid: e.target.value })} />
          <Input data-testid="sms-token" type="password" placeholder="Auth Token" value={f.auth_token} onChange={(e) => setF({ ...f, auth_token: e.target.value })} />
          <Input data-testid="sms-from" placeholder="From number (+1...)" value={f.from_number} onChange={(e) => setF({ ...f, from_number: e.target.value })} />
          <Input data-testid="sms-verify-sid" placeholder="Verify Service SID (VA..., optional)" value={f.verify_sid} onChange={(e) => setF({ ...f, verify_sid: e.target.value })} />
        </div>
        <div className="flex flex-wrap gap-2">
          <Button data-testid="sms-save" size="sm" className="bg-slate-900 hover:bg-slate-800" disabled={!f.account_sid} onClick={save}>Save Twilio settings</Button>
          <Input data-testid="sms-test-to" className="w-44" placeholder="Test to (+91...)" value={testTo} onChange={(e) => setTestTo(e.target.value)} />
          <Button data-testid="sms-test-btn" size="sm" variant="outline" disabled={!testTo} onClick={test}>Send test SMS</Button>
        </div>
      </div>
      <div className="bg-white border border-slate-200 rounded-xl p-5" data-testid="sms-log-panel">
        <p className="font-display font-bold mb-2">SMS log</p>
        {(log || []).length === 0 && <p className="text-sm text-slate-400">No messages yet.</p>}
        {(log || []).map((l) => <p key={l.id} className="text-xs text-slate-600 border-b border-slate-50 py-1">{l.at?.slice(0, 16)} · {l.to} · {l.status} · {l.body?.slice(0, 60)}</p>)}
      </div>
    </div>
  );
}

export default function Integrations() {
  const [tab, setTab] = useState('plugins');
  const { data, refetch } = useQuery({ queryKey: ['int-catalog'], queryFn: () => api.get('/integrations/catalog').then((r) => r.data) });
  return (
    <div className="space-y-5" data-testid="integrations-page">
      <div>
        <h1 className="font-display text-2xl font-extrabold tracking-tight flex items-center gap-2"><PlugsConnected size={26} className="text-orange-600" />Integrations</h1>
        <p className="text-sm text-slate-500">Zoho plugins, REST API keys, webhooks & SCIM - connect ONRID to the tools you already use.</p>
      </div>
      <div className="flex gap-2">
        {[['plugins', 'Plugins'], ['payments', 'Payments (Razorpay)'], ['sms', 'SMS (Twilio)'], ['api', 'API keys'], ['scim', 'SCIM 2.0']].map(([k, l]) => (
          <button key={k} data-testid={`integrations-tab-${k}`} onClick={() => setTab(k)}
            className={`px-3 py-1.5 rounded-full text-sm ${tab === k ? 'bg-slate-900 text-white' : 'bg-white border border-slate-200 text-slate-600'}`}>{l}</button>
        ))}
      </div>
      {tab === 'plugins' && <div className="grid lg:grid-cols-2 gap-4">{(data?.plugins || []).map((p) => <PluginCard key={p.key} p={p} refetch={refetch} />)}</div>}
      {tab === 'payments' && <PaymentsPanel />}
      {tab === 'sms' && <SmsPanel />}
      {tab === 'api' && <ApiKeys />}
      {tab === 'scim' && <ScimPanel />}
    </div>
  );
}
